import { useState, useEffect, useMemo, useRef } from 'react';
import { Listing, ListingType } from '../../types/index';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import Map from './Map';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalendarDay, FaCalendarWeek, FaCalendarAlt, FaClock, FaSearch, FaTimes, FaChevronDown, FaChevronUp, FaExclamationTriangle } from 'react-icons/fa';

const ITEMS_PER_PAGE = 10;

/**
 * How long an undated listing stays visible. Scraped posts often carry no
 * parseable sale date; they are shown for a while rather than discarded, but
 * they stop being a plausible "current find" well before this.
 */
const UNDATED_MAX_AGE_DAYS = 60;

const listingVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const dateOptions = [
  { label: 'Any Date', value: '', icon: <FaCalendarAlt className="inline mr-1" /> },
  { label: 'Today', value: 'today', icon: <FaCalendarDay className="inline mr-1" /> },
  { label: 'This Week', value: 'this-week', icon: <FaCalendarWeek className="inline mr-1" /> },
  { label: 'Upcoming', value: 'upcoming', icon: <FaClock className="inline mr-1" /> },
];

function getDateRange(option: string) {
  const today = new Date();
  if (option === 'today') {
    const dateStr = today.toISOString().split('T')[0];
    return { from: dateStr, to: dateStr };
  }
  if (option === 'this-week') {
    const first = today.getDate() - today.getDay();
    const last = first + 6;
    const start = new Date(today.setDate(first));
    const end = new Date(today.setDate(last));
    return {
      from: start.toISOString().split('T')[0],
      to: end.toISOString().split('T')[0],
    };
  }
  if (option === 'upcoming') {
    const from = today.toISOString().split('T')[0];
    return { from, to: '' };
  }
  return { from: '', to: '' };
}

// Create motion components
const MotionDiv = motion.create('div');

export default function ListingList() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [selectedType, setSelectedType] = useState<ListingType | ''>('');
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [viewMode, setViewMode] = useState<'split' | 'map' | 'list'>('split');
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'title-asc' | 'title-desc'>('date-desc');
  const [dateOption, setDateOption] = useState('');
  const [freeOrSale, setFreeOrSale] = useState(''); // '', 'free', 'sale'
  const [currentPage, setCurrentPage] = useState(1);
  const [mapError, setMapError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const selectedListingRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchListings();
  }, []);

  // Scroll to selected listing when it changes
  useEffect(() => {
    if (selectedListing && selectedListingRef.current) {
      selectedListingRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [selectedListing]);

  const fetchListings = async () => {
    try {
      // A sale that has already ended is not a find. Undated listings are kept
      // only while they are recent: an unknown date is not evidence the sale is
      // over, but a year-old undated post is not a current find either.
      const today = new Date().toISOString().slice(0, 10);
      const undatedCutoff = new Date(Date.now() - UNDATED_MAX_AGE_DAYS * 86_400_000).toISOString();

      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('status', 'approved')
        // The same sale can be listed on more than one source site. Duplicates
        // point at the canonical row via duplicate_of; show only canonical ones.
        .is('duplicate_of', null)
        .or(`date_range_end.gte.${today},and(date_range_end.is.null,created_at.gte.${undatedCutoff})`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (error: any) {
      toast.error('Error fetching listings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper to determine if a listing is free or sale
  function getFreeOrSale(listing: Listing) {
    const text = `${listing.title} ${listing.description}`.toLowerCase();
    if (text.includes('free')) return 'free';
    if (text.includes('sale')) return 'sale';
    return '';
  }

  const filteredListings = useMemo(() => {
    return listings
      .filter((listing) => {
        // Type filter
        if (selectedType && listing.type !== selectedType) return false;
        
        // Date filter
        const { from, to } = getDateRange(dateOption);
        if (from && listing.date_range_start && new Date(listing.date_range_start) < new Date(from)) return false;
        if (to && listing.date_range_end && new Date(listing.date_range_end) > new Date(to)) return false;
        
        // Free/Sale filter
        if (freeOrSale && getFreeOrSale(listing) !== freeOrSale) return false;

        // Search query filter
        if (searchQuery) {
          const searchLower = searchQuery.toLowerCase();
          const listingText = `${listing.title} ${listing.description} ${listing.location}`.toLowerCase();
          
          // Check for date keywords
          if (searchLower === 'today') {
            const today = new Date().toISOString().split('T')[0];
            return (listing.date_range_start && listing.date_range_start === today) || 
                   (listing.date_range_end && listing.date_range_end === today);
          }
          if (searchLower === 'tomorrow') {
            const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
            return (listing.date_range_start && listing.date_range_start === tomorrow) || 
                   (listing.date_range_end && listing.date_range_end === tomorrow);
          }
          if (searchLower === 'this week') {
            const today = new Date();
            const first = today.getDate() - today.getDay();
            const last = first + 6;
            const start = new Date(today.setDate(first)).toISOString().split('T')[0];
            const end = new Date(today.setDate(last)).toISOString().split('T')[0];
            return (listing.date_range_start && listing.date_range_start >= start && listing.date_range_start <= end) ||
                   (listing.date_range_end && listing.date_range_end >= start && listing.date_range_end <= end);
          }
          
          // Regular keyword search
          return listingText.includes(searchLower);
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'date-desc') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        if (sortBy === 'date-asc') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        if (sortBy === 'title-asc') return a.title.localeCompare(b.title);
        if (sortBy === 'title-desc') return b.title.localeCompare(a.title);
        return 0;
      });
  }, [listings, selectedType, dateOption, freeOrSale, sortBy, searchQuery]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredListings.length / ITEMS_PER_PAGE);
  const paginatedListings = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredListings.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredListings, currentPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType, dateOption, freeOrSale, sortBy]);

  // Clear selected listing when it's filtered out of the results
  useEffect(() => {
    if (selectedListing && !filteredListings.some(listing => listing.id === selectedListing.id)) {
      setSelectedListing(null);
    }
  }, [filteredListings, selectedListing]);

  // Handle view mode changes with a more aggressive map refresh
  useEffect(() => {
    // First immediate resize
    window.dispatchEvent(new Event('resize'));
    
    // Then a series of delayed resizes to ensure map renders correctly
    const timeouts = [100, 300, 500].map(delay => 
      setTimeout(() => window.dispatchEvent(new Event('resize')), delay)
    );
    
    return () => timeouts.forEach(t => clearTimeout(t));
  }, [viewMode]);

  // Force map resize when filters change, especially date filter
  useEffect(() => {
    if (viewMode === 'map' || viewMode === 'split') {
      // Use a longer timeout for date filters, especially "Today" to ensure map is properly displayed
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        
        // For date filters that might result in no listings, dispatch another resize after a delay
        if (dateOption && filteredListings.length === 0) {
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
          }, 300);
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [dateOption, filteredListings, viewMode]);

  // Handle window resize when view mode changes
  useEffect(() => {
    if (viewMode === 'map' || viewMode === 'split') {
      // Add a slight delay to ensure DOM is updated before map resize
      const timer = setTimeout(() => {
        // Dispatch resize event to force map to update its dimensions
        window.dispatchEvent(new Event('resize'));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [viewMode]);

  // Ensure selectedListing doesn't cause re-rendering issues
  const handleListingSelect = (listing: Listing) => {
    // Only set if different to avoid unnecessary re-renders
    if (!selectedListing || selectedListing.id !== listing.id) {
      setSelectedListing(listing);
      
      // Also dispatch a resize event to ensure map properly redraws
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
      
      toast.success(`Jumped to ${listing.title}`, {
        duration: 2000,
        position: 'bottom-center',
      });
    }
  };

  const handleMapError = (error: string) => {
    setMapError(error);
    toast.error('Error loading map: ' + error);
  };

  // Check if any filters are active
  const hasActiveFilters = selectedType !== '' || dateOption !== '' || freeOrSale !== '' || searchQuery !== '';

  // Toggle filter collapse
  const toggleFilters = () => {
    setFiltersCollapsed(!filtersCollapsed);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="loading-spinner" role="status" aria-label="Loading listings">
          <span className="visually-hidden">Loading listings...</span>
        </div>
      </div>
    );
  }

  const EmptyState = () => (
    <MotionDiv 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center h-full text-center p-8"
    >
      <div className="w-24 h-24 mb-4 text-orange-500">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Listings Found</h3>
      <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md">
        Be the first to add a listing in your area! Share your garage sale, estate sale, or curb alert with the community.
      </p>
      <Link
        to="/create"
        className="btn btn-primary"
      >
        Add a Listing
      </Link>
    </MotionDiv>
  );

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Sticky Controls */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col gap-4 w-full px-2 sm:px-4">
          <div className="flex justify-between items-center">
            <button 
              onClick={toggleFilters}
              className="flex items-center text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 text-sm font-medium focus:outline-none focus-ring"
              aria-expanded={!filtersCollapsed}
              aria-controls="filters-section"
            >
              {filtersCollapsed ? (
                <>
                  <FaChevronDown className="mr-1" />
                  <span>Show Filters</span>
                  {hasActiveFilters && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                      <FaExclamationTriangle className="mr-1" /> Filters Applied
                    </span>
                  )}
                </>
              ) : (
                <>
                  <FaChevronUp className="mr-1" />
                  <span>Hide Filters</span>
                </>
              )}
            </button>
            
            <div className="flex items-center gap-2">
              <div className="flex rounded-md shadow-sm" role="group" aria-label="View mode selection">
                <button
                  onClick={() => setViewMode('split')}
                  className={`px-3 py-2 text-sm font-medium rounded-l-md ${
                    viewMode === 'split'
                      ? 'bg-orange-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                  aria-pressed={viewMode === 'split'}
                >
                  Split
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-3 py-2 text-sm font-medium ${
                    viewMode === 'map'
                      ? 'bg-orange-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                  aria-pressed={viewMode === 'map'}
                >
                  Map
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 text-sm font-medium rounded-r-md ${
                    viewMode === 'list'
                      ? 'bg-orange-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                  aria-pressed={viewMode === 'list'}
                >
                  List
                </button>
              </div>
              <Link
                to="/create"
                className="btn btn-primary"
              >
                Add Listing
              </Link>
            </div>
          </div>
          
          {/* Collapsible Filters */}
          <AnimatePresence>
            {!filtersCollapsed && (
              <motion.div
                id="filters-section"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap md:flex-row md:items-center gap-2 w-full">
                  {/* Search Bar */}
                  <div className="relative flex-1 min-w-[200px] max-w-md">
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search listings, dates (today, tomorrow, this week)..."
                        className="input pl-10 pr-10 w-full"
                        aria-label="Search listings"
                      />
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      {searchQuery && (
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            searchInputRef.current?.focus();
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          aria-label="Clear search"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Filter Group */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Listing Type Filter */}
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value as ListingType | '')}
                      className="input min-w-[120px]"
                      aria-label="Filter by listing type"
                    >
                      <option value="">All Types</option>
                      <option value="garage-sale">Garage Sales</option>
                      <option value="estate-sale">Estate Sales</option>
                      <option value="curb-alert">Curb Alerts</option>
                    </select>
                    
                    {/* Date Filter - Now a dropdown */}
                    <select
                      value={dateOption}
                      onChange={(e) => setDateOption(e.target.value)}
                      className="input min-w-[120px]"
                      aria-label="Filter by date"
                    >
                      {dateOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    
                    {/* Price Filter */}
                    <select
                      value={freeOrSale}
                      onChange={e => setFreeOrSale(e.target.value)}
                      className="input min-w-[90px]"
                      aria-label="Filter by price type"
                    >
                      <option value="">All</option>
                      <option value="free">Free</option>
                      <option value="sale">Sale</option>
                    </select>
                    
                    {/* Sort Order */}
                    <select
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value as any)}
                      className="input min-w-[90px]"
                      aria-label="Sort listings"
                    >
                      <option value="date-desc">Newest</option>
                      <option value="date-asc">Oldest</option>
                      <option value="title-asc">A-Z</option>
                      <option value="title-desc">Z-A</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Map Section - Always render but conditionally show/hide based on viewMode */}
        <div 
          className={`${
            viewMode === 'split' ? 'w-full md:w-1/2' : 
            viewMode === 'map' ? 'w-full' : 'hidden'
          } h-[50vh] md:h-full`}
        >
          <div className="w-full h-full" id="map-wrapper">
            <Map 
              listings={filteredListings} 
              selectedListing={selectedListing} 
              onListingSelect={handleListingSelect}
              onError={handleMapError}
              // Use a fixed key to prevent remounting the map component
              key="map-component-fixed" 
            />
          </div>
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-80 z-10 p-4">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow-lg max-w-md text-center">
                <h3 className="text-lg font-medium text-red-600 dark:text-red-400 mb-2">Map Error</h3>
                <p className="text-gray-600 dark:text-gray-300">{mapError}</p>
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  Please check your internet connection and refresh the page.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Listings Section */}
        {(viewMode === 'split' || viewMode === 'list') && (
          <div className={`${viewMode === 'split' ? 'w-full md:w-1/2' : 'w-full'} h-[50vh] md:h-full overflow-y-auto bg-white dark:bg-gray-800 p-6`}>
            {filteredListings.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                <AnimatePresence>
                  <div className="space-y-4">
                    {paginatedListings.map((listing) => (
                      <motion.div
                        key={listing.id}
                        variants={listingVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        transition={{ duration: 0.2 }}
                        ref={selectedListing?.id === listing.id ? selectedListingRef : null}
                        onClick={() => handleListingSelect(listing)}
                        className={`list-item border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${
                          selectedListing?.id === listing.id 
                            ? 'ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-900/20 scale-[1.02]' 
                            : ''
                        }`}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleListingSelect(listing);
                          }
                        }}
                      >
                        <div className="flex gap-4">
                          {listing.images && listing.images.length > 0 ? (
                            <img 
                              src={listing.images[0]} 
                              alt={`Thumbnail for ${listing.title}`} 
                              className="w-16 h-16 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center text-gray-400">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5V5.25C3 4.007 4.007 3 5.25 3h13.5C19.993 3 21 4.007 21 5.25v13.5c0 1.243-1.007 2.25-2.25 2.25H5.25A2.25 2.25 0 013 18.75V16.5zm0 0l5.25-5.25a2.25 2.25 0 013.182 0l5.318 5.318M3 16.5l3.75-3.75a2.25 2.25 0 013.182 0l2.068 2.068" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{listing.title}</h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{listing.description}</p>
                            <div className="mt-2 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="status-badge bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                  {listing.type}
                                </span>
                                <span className={`status-badge ${
                                  getFreeOrSale(listing) === 'free' 
                                    ? 'status-badge-free'
                                    : 'status-badge-sale'
                                }`}>
                                  {getFreeOrSale(listing) || 'Price Varies'}
                                </span>
                              </div>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {new Date(listing.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </AnimatePresence>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * ITEMS_PER_PAGE, filteredListings.length)}
                        </span>{' '}
                        of <span className="font-medium">{filteredListings.length}</span> results
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="btn btn-secondary"
                        aria-label="Previous page"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="btn btn-secondary"
                        aria-label="Next page"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 